import { useEffect } from "react";

/**
 * Injects Organization + WebSite JSON-LD schema into <head> once,
 * globally. Mounted on the Landing page so it lives at the canonical
 * homepage URL — the entity anchor every LLM cross-references.
 *
 * `sameAs` binds this Organization to its identities on every
 * directory + social profile the founder registers. Update this
 * list as new listings go live (Product Hunt, Wikidata, G2, etc.).
 *
 * WebSite schema exposes the sitelinks search box + potential
 * primary URL to Google's structured data crawler.
 */
export default function OrganizationSchema() {
  useEffect(() => {
    const org = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://facetra.studio/#organization",
      name: "Facetra",
      legalName: "Facetra Design Studio Private Limited",
      url: "https://facetra.studio/",
      logo: {
        "@type": "ImageObject",
        url: "https://facetra.studio/logo.png",
      },
      description:
        "AI-powered jewellery design workflow platform for Indian ateliers. Converts hand sketches into photorealistic renders, multi-view technical sheets, CAD-ready references and 3D meshes for Jadau, Polki, Kundan, Meenakari, Temple and Rajwadi jewellery styles.",
      foundingDate: "2025",
      address: {
        "@type": "PostalAddress",
        addressCountry: "IN",
      },
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: "hello@facetra.studio",
          areaServed: "IN",
          availableLanguage: ["en", "hi"],
        },
      ],
      sameAs: [
        // Update this list as directory listings go live. Every URL
        // added here strengthens the entity binding for LLMs — they
        // treat co-occurrence across these surfaces as identity proof.
        "https://www.linkedin.com/company/facetra",
        // "https://www.producthunt.com/products/facetra",
        // "https://www.crunchbase.com/organization/facetra",
        // "https://www.wikidata.org/wiki/QXXXXXX",
        // "https://alternativeto.net/software/facetra/",
        // "https://theresanaiforthat.com/ai/facetra/",
        // "https://twitter.com/facetra_studio",
      ],
      knowsAbout: [
        "Jadau jewellery",
        "Polki jewellery",
        "Kundan setting",
        "Meenakari enamel",
        "Temple jewellery",
        "Rajwadi jewellery",
        "AI jewellery design",
        "CAD-ready jewellery rendering",
        "Multi-view technical sheets",
        "Bill of Materials",
        "Sketch-to-3D workflow",
      ],
    };

    const website = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://facetra.studio/#website",
      url: "https://facetra.studio/",
      name: "Facetra",
      publisher: { "@id": "https://facetra.studio/#organization" },
      inLanguage: "en-IN",
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: "https://facetra.studio/articles?q={search_term_string}",
        },
        "query-input": "required name=search_term_string",
      },
    };

    const attach = (data, key) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.orgSchema = key;
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    };
    const s1 = attach(org, "organization");
    const s2 = attach(website, "website");

    return () => {
      s1.remove();
      s2.remove();
    };
  }, []);

  return null;
}
